/**
 * Shared pointer play for dot backgrounds, from hung-hola's assets/js/dot-ripple.js (owner, 2026-10-20;
 * reference: growtrics.ai), without GSAP (requestAnimationFrame).
 * - Hover (mouse), one of HOVERS per grid; it eases after the pointer and fades away when it leaves:
 *   - spotlight (the default; owner 2026-10-21, the push felt like drilling a hole in a big area): the dots stay put,
 *     the ones under the pointer brighten and grow a little, softly less out to its radius;
 *   - push (small areas, Get in touch; owner 2026-10-21): one small circle round the pointer pushes the dots out to
 *     its rim.
 * - Click on the area (not on a link, button or field): a light sweep (owner 2026-10-21, after the water drop's rings
 *   were too turbulent): the dots stay put, a soft ring of light runs out from the point, each dot brightening and
 *   growing a little as it passes.
 * It all runs on the GPU per dot: the vertex shader calls pxDotStir(screenPx). A dot shader on a 3D surface (the hero's
 * sheet) uses dotStir() for the pointer alone: stir.pointer() for the hover, { onDrop } for the clicks, and places both
 * on its surface itself.
 * Only with a mouse, never with ?still or reduced motion (dotsPlay()): phones and tablets keep the still CSS dots.
 *
 *   blockDots(canvas, area, options, host) // a block's dots (Portfolio, Get in touch): false = its CSS dots stay
 *   dotGrid(canvas, area, options)         // the grid alone, false without WebGL2
 *   DOT_STIR_GLSL + dotStir(area, canvas, frame, { hover }) // the play on another flat dot shader
 */
import { isStill } from './motion';

// radius: px, where it has faded out; push: px a dot at the centre is pushed out; lift: how far the opacity of a dot
// under the pointer goes towards full; grow: its extra size
export const HOVERS = {
	spotlight: { radius: 120, push: 0, lift: 0.8, grow: 0.35 },
	push: { radius: 80, push: 22, lift: 0, grow: 0.5 },
};
export type Hover = keyof typeof HOVERS;

export const DOTS = {
	// the mouse (its look: HOVERS)
	hoverFollow: 0.18, // per 60 fps frame: how fast the light catches the pointer
	hoverFade: 0.06, // per 60 fps frame: how fast it fades in / out
	// a click: the light sweep
	sweepSpeed: 380, // px per second the ring of light runs out
	sweepWidth: 36, // px, the ring's softness (half its width)
	sweepLift: 0.7, // how far a lit dot's opacity goes towards full
	sweepGrow: 0.4, // extra dot size on the ring
	sweepLife: 1.4, // s until a sweep has faded out
	maxDrops: 4, // clicks at once (the oldest gives way)
};

export const DOT_STIR_GLSL = `
uniform vec4 uDotHover;    // pointer x, y (device px, canvas), radius, amount 0..1
uniform vec3 uDotHoverFx;  // lift, grow, push px
uniform vec4 uDotDrops[4]; // click x, y, age s, alive (0 / 1)
uniform vec4 uDotSweep;    // speed px/s, softness px, grow, life s
uniform float uDotLift;    // the sweep's lift
// x, y: push (px); z: extra size; w: light 0..1 (the shader lifts the dot's opacity towards full by it)
vec4 pxDotStir(vec2 p) {
  vec2 d = p - uDotHover.xy;
  float dist = length(d);
  float t = dist / max(uDotHover.z, 1.0);
  float spot = (1.0 - smoothstep(0.0, 1.0, t)) * uDotHover.w;
  vec2 off = dist > 0.001 ? d / dist * spot * uDotHoverFx.z : vec2(0.0);
  float sweep = 0.0;
  for (int i = 0; i < 4; i++) {
    vec4 drop = uDotDrops[i];
    if (drop.w < 0.5) continue;
    float behind = (drop.z * uDotSweep.x - length(p - drop.xy)) / max(uDotSweep.y, 1.0);
    float life = pow(1.0 - clamp(drop.z / uDotSweep.w, 0.0, 1.0), 2.0);
    sweep = max(sweep, exp(-behind * behind) * life);
  }
  return vec4(off, spot * uDotHoverFx.y + sweep * uDotSweep.z, max(spot * uDotHoverFx.x, sweep * uDotLift));
}`;

type Drop = { x: number; y: number; age: number };

export type DotStir = {
	update: (dt: number) => void;
	upload: (gl: WebGL2RenderingContext, program: WebGLProgram) => void;
	busy: () => boolean;
	pointer: () => { x: number; y: number; amount: number }; // the eased pointer, client px, and its fade 0..1
};

// frame: the drawing's top-left from the canvas's, client px, and its device pixel ratio (the hero draws a 16:9 frame
// cropped to the canvas; a grid: 0, 0)
export type StirFrame = { x: number; y: number; dpr: number };
// hover: its look (HOVERS); onDrop: a click handed to the caller instead of the light sweep (client px)
export type StirOptions = { hover?: Hover; onDrop?: (x: number, y: number) => void };

/**
 * The pointer play on any dot shader: include DOT_STIR_GLSL in its vertex shader, call pxDotStir(screenPx) with the
 * dot's screen position (device px), add .xy to it, multiply its size by 1 + .z, lift its opacity by .w. Each frame:
 * stir.update(dt); stir.upload(gl, program). With onDrop the clicks go there, and none light the dots.
 */
export function dotStir(area: HTMLElement, canvas: HTMLCanvasElement, frame: () => StirFrame, options: StirOptions = {}): DotStir {
	const { onDrop } = options;
	const look = HOVERS[options.hover ?? 'spotlight'];
	const hover = { x: 0, y: 0, tx: 0, ty: 0, on: false, amount: 0 };
	const drops: Drop[] = [];
	let known = false;
	const over = (): boolean => {
		const box = area.getBoundingClientRect();
		return hover.tx >= box.left && hover.tx <= box.right && hover.ty >= box.top && hover.ty <= box.bottom;
	};

	window.addEventListener(
		'pointermove',
		(e) => {
			if (e.pointerType !== 'mouse') {
				return;
			}
			hover.tx = e.clientX;
			hover.ty = e.clientY;
			known = true;
			hover.on = over();
			// a fresh entry starts under the pointer instead of sliding in from the old spot
			if (hover.on && hover.amount < 0.01) {
				hover.x = e.clientX;
				hover.y = e.clientY;
			}
		},
		{ passive: true }
	);
	document.documentElement.addEventListener('pointerleave', () => {
		hover.on = false;
		known = false;
	});
	// a press on the background, not on a link, button or field of the section
	area.addEventListener('pointerdown', (e) => {
		const target = e.target as Element | null;
		if (e.button !== 0 || target?.closest('a, button, input, textarea, select, label, form, [data-dots-ignore]')) {
			return;
		}
		if (onDrop) {
			onDrop(e.clientX, e.clientY);
			return;
		}
		drops.push({ x: e.clientX, y: e.clientY, age: 0 });
		if (drops.length > DOTS.maxDrops) {
			drops.shift();
		}
	});

	let scrollX = window.scrollX;
	let scrollY = window.scrollY;
	const update = (dt: number): void => {
		if (known) {
			// the page scrolling under a still mouse moves the area in or out: fade, never pop
			hover.on = over();
			if (hover.on && hover.amount < 0.01) {
				hover.x = hover.tx;
				hover.y = hover.ty;
			}
		}
		const frames = dt * 60;
		// drops are kept in client px: follow the page when it scrolls under them
		const sx = window.scrollX - scrollX;
		const sy = window.scrollY - scrollY;
		scrollX = window.scrollX;
		scrollY = window.scrollY;
		drops.forEach((drop) => {
			drop.x -= sx;
			drop.y -= sy;
			drop.age += dt;
		});
		while (drops.length && drops[0].age > DOTS.sweepLife) {
			drops.shift();
		}
		const follow = 1 - Math.pow(1 - DOTS.hoverFollow, frames);
		hover.x += (hover.tx - hover.x) * follow;
		hover.y += (hover.ty - hover.y) * follow;
		const fade = 1 - Math.pow(1 - DOTS.hoverFade, frames);
		hover.amount += ((hover.on ? 1 : 0) - hover.amount) * fade;
		if (hover.amount < 0.001) {
			hover.amount = 0;
		}
	};

	const locations = new WeakMap<WebGLProgram, Record<string, WebGLUniformLocation | null>>();
	const upload = (gl: WebGL2RenderingContext, program: WebGLProgram): void => {
		let u = locations.get(program);
		if (!u) {
			const at = (name: string) => gl.getUniformLocation(program, name);
			u = { hover: at('uDotHover'), hoverFx: at('uDotHoverFx'), drops: at('uDotDrops'), sweep: at('uDotSweep'), lift: at('uDotLift') };
			locations.set(program, u);
		}
		const box = canvas.getBoundingClientRect();
		const { x, y, dpr: s } = frame();
		const left = box.left + x;
		const top = box.top + y;
		gl.uniform4f(u.hover, (hover.x - left) * s, (hover.y - top) * s, look.radius * s, hover.amount);
		gl.uniform3f(u.hoverFx, look.lift, look.grow, look.push * s);
		const data = new Float32Array(16);
		drops.forEach((drop, i) => data.set([(drop.x - left) * s, (drop.y - top) * s, drop.age, 1], i * 4));
		gl.uniform4fv(u.drops, data);
		gl.uniform4f(u.sweep, DOTS.sweepSpeed * s, DOTS.sweepWidth * s, DOTS.sweepGrow, DOTS.sweepLife);
		gl.uniform1f(u.lift, DOTS.sweepLift);
	};

	return {
		update,
		upload,
		busy: (): boolean => hover.amount > 0 || hover.on || drops.length > 0,
		pointer: () => ({ x: hover.x, y: hover.y, amount: hover.amount }),
	};
}

type GridOptions = {
	cols: number; // dots per row, the first and last at the edges
	rows: number;
	gapY: number; // px between rows
	size: number; // px, a dot
	color: [number, number, number, number]; // r, g, b, a 0..1
	inset: number; // px the canvas reaches past the dots on every side (grown or pushed dots stay whole)
	hover?: Hover; // the mouse's look, HOVERS (default spotlight)
};

/**
 * A fixed grid of round dots. Draws once, then only while the pointer plays with it; off screen it sleeps.
 */
export function dotGrid(canvas: HTMLCanvasElement, area: HTMLElement, options: GridOptions): boolean {
	const { cols, rows, gapY, size, color, inset, hover } = options;
	const gl = canvas.getContext('webgl2', { premultipliedAlpha: true, antialias: false });
	if (!gl) {
		return false;
	}
	const vs = `#version 300 es
in vec2 aPos;
uniform vec2 uView;
uniform float uDpr;
uniform float uSize;
out float vLight;
${DOT_STIR_GLSL}
void main() {
  vec2 p = aPos * uDpr;
  vec4 stir = pxDotStir(p);
  vLight = stir.w;
  p += stir.xy;
  gl_Position = vec4(p.x / uView.x * 2.0 - 1.0, 1.0 - p.y / uView.y * 2.0, 0.0, 1.0);
  gl_PointSize = uSize * uDpr * (1.0 + stir.z);
}`;
	const fs = `#version 300 es
precision highp float;
uniform vec4 uColor;
in float vLight;
out vec4 outColor;
void main() {
  float d = length(gl_PointCoord - 0.5);
  float a = (1.0 - smoothstep(0.42, 0.5, d)) * mix(uColor.a, 1.0, vLight);
  if (a <= 0.002) discard;
  outColor = vec4(uColor.rgb * a, a);
}`;
	const program = createProgram(gl, vs, fs);
	gl.useProgram(program);
	const at = (name: string) => gl.getUniformLocation(program, name);
	const buffer = gl.createBuffer();
	const loc = gl.getAttribLocation(program, 'aPos');
	let count = 0;
	let dpr = 1;
	const stir = dotStir(area, canvas, () => ({ x: 0, y: 0, dpr }), { hover });

	const draw = (): void => {
		gl.clearColor(0, 0, 0, 0);
		gl.clear(gl.COLOR_BUFFER_BIT);
		gl.enable(gl.BLEND);
		gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
		gl.uniform2f(at('uView'), canvas.width, canvas.height);
		gl.uniform1f(at('uDpr'), dpr);
		gl.uniform1f(at('uSize'), size);
		gl.uniform4f(at('uColor'), ...color);
		stir.upload(gl, program);
		gl.drawArrays(gl.POINTS, 0, count);
	};
	const resize = (): void => {
		dpr = Math.min(2, window.devicePixelRatio);
		const w = canvas.clientWidth;
		canvas.width = Math.round(w * dpr);
		canvas.height = Math.round(canvas.clientHeight * dpr);
		gl.viewport(0, 0, canvas.width, canvas.height);
		const points: number[] = [];
		// dot centres like the CSS grid they replace: half a dot in from each cell's top left
		const gapX = (w - 2 * inset - size) / Math.max(1, cols - 1);
		for (let r = 0; r < rows; r++) {
			for (let c = 0; c < cols; c++) {
				points.push(inset + size / 2 + c * gapX, inset + size / 2 + r * gapY);
			}
		}
		count = points.length / 2;
		gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
		gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(points), gl.STATIC_DRAW);
		gl.enableVertexAttribArray(loc);
		gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
		draw();
	};

	let inView = false;
	let wasBusy = false;
	let last = 0;
	const tick = (now: number): void => {
		if (!inView) {
			last = 0;
			return;
		}
		stir.update(last ? Math.min(0.1, (now - last) / 1000) : 0);
		last = now;
		const busy = stir.busy();
		if (busy || wasBusy) {
			draw(); // one more frame after it settles, so the last state is the rest state
		}
		wasBusy = busy;
		requestAnimationFrame(tick);
	};
	new IntersectionObserver(([entry]) => {
		const was = inView;
		inView = entry.isIntersecting;
		if (inView && !was) {
			requestAnimationFrame(tick);
		}
	}).observe(canvas);
	new ResizeObserver(resize).observe(canvas);
	return true;
}

/** The dots play only with a mouse (hover + a fine pointer), never with ?still or reduced motion. */
export const dotsPlay = (): boolean => !isStill() && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

/**
 * A block's dots: the grid on its canvas, has-dots on host once it draws (the block's CSS then hides its still dots).
 * false, the CSS dots left, without a mouse, with ?still or reduced motion, without WebGL2 or if a shader fails.
 */
export function blockDots(canvas: HTMLCanvasElement, area: HTMLElement, options: GridOptions, host: HTMLElement): boolean {
	let drawn = false;
	if (dotsPlay()) {
		try {
			drawn = dotGrid(canvas, area, options);
		} catch (e) {
			console.warn('[dots] dot grid disabled:', (e as Error).message);
		}
	}
	host.classList.toggle('has-dots', drawn);
	return drawn;
}

export function createProgram(gl: WebGLRenderingContext | WebGL2RenderingContext, vertex: string, fragment: string): WebGLProgram {
	const compile = (type: number, source: string): WebGLShader => {
		const shader = gl.createShader(type) as WebGLShader;
		gl.shaderSource(shader, source);
		gl.compileShader(shader);
		if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
			throw new Error(gl.getShaderInfoLog(shader) || 'shader');
		}
		return shader;
	};
	const program = gl.createProgram() as WebGLProgram;
	gl.attachShader(program, compile(gl.VERTEX_SHADER, vertex));
	gl.attachShader(program, compile(gl.FRAGMENT_SHADER, fragment));
	gl.linkProgram(program);
	if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
		throw new Error(gl.getProgramInfoLog(program) || 'program');
	}
	return program;
}
